import { useSelector } from "react-redux"
import { useMemo } from "react"
import {
  userPermissionsSelector,
  activeProfileSelector,
  hasFullAccessSelector
} from "store/user"
import {
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
  hasAllPermissions as checkAllPermissions,
  hasModuleAccess as checkModuleAccess,
  getPermissionScope as getScope,
  getActivePermissions as getActive,
  hasFullAccess as checkFullAccess
} from "utils/permissions"

/**
 * Custom hook to access and check user permissions
 * @returns {Object} Permission checking functions and data
 */
export const usePermissions = () => {
  const permissions = useSelector(userPermissionsSelector)
  const activeProfile = useSelector(activeProfileSelector)
  const hasFullAccessFromSelector = useSelector(hasFullAccessSelector)

  // Memoize permission checking functions
  const permissionUtils = useMemo(() => {
    return {
      /**
       * Check if user has permission for a specific module and action
       * @param {string} moduleName - The module name (e.g., "bookings", "calendar")
       * @param {string} actionName - The action name (e.g., "view", "create", "edit")
       * @param {string} screenName - Optional screen name
       * @returns {boolean}
       */
      hasPermission: (moduleName, actionName, screenName = null) => {
        // If user has full access, grant all permissions
        if (hasFullAccessFromSelector || checkFullAccess(activeProfile)) {
          return true
        }
        return checkPermission(permissions, moduleName, actionName, screenName)
      },

      /**
       * Check if user has any of the specified permissions
       * @param {Array} checks - Array of permission checks [{module, action, screen?}]
       * @returns {boolean}
       */
      hasAnyPermission: (checks) => {
        if (hasFullAccessFromSelector || checkFullAccess(activeProfile)) {
          return true
        }
        return checkAnyPermission(permissions, checks)
      },

      /**
       * Check if user has all of the specified permissions
       * @param {Array} checks - Array of permission checks [{module, action, screen?}]
       * @returns {boolean}
       */
      hasAllPermissions: (checks) => {
        if (hasFullAccessFromSelector || checkFullAccess(activeProfile)) {
          return true
        }
        return checkAllPermissions(permissions, checks)
      },

      /**
       * Check if user has access to a specific module
       * @param {string} moduleName - The module name
       * @param {string} screenName - Optional screen name
       * @returns {boolean}
       */
      hasModuleAccess: (moduleName, screenName = null) => {
        if (hasFullAccessFromSelector || checkFullAccess(activeProfile)) {
          return true
        }
        return checkModuleAccess(permissions, moduleName, screenName)
      },

      /**
       * Get permission scope for a specific module and action
       * @param {string} moduleName - The module name
       * @param {string} actionName - The action name
       * @param {string} screenName - Optional screen name
       * @returns {Array}
       */
      getPermissionScope: (moduleName, actionName, screenName = null) => {
        return getScope(permissions, moduleName, actionName, screenName)
      },

      /**
       * Get all active permissions for a module
       * @param {string} moduleName - The module name
       * @param {string} screenName - Optional screen name
       * @returns {Array}
       */
      getActivePermissions: (moduleName, screenName = null) => {
        return getActive(permissions, moduleName, screenName)
      },

      /**
       * Check if user has full access (admin/owner)
       * @returns {boolean}
       */
      hasFullAccess: () => {
        return hasFullAccessFromSelector || checkFullAccess(activeProfile)
      }
    }
  }, [permissions, activeProfile, hasFullAccessFromSelector])

  return {
    ...permissionUtils,
    permissions,
    activeProfile,
    hasFullAccessFlag: hasFullAccessFromSelector || checkFullAccess(activeProfile)
  }
}
